
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Modal,
  ModalBody,
  Button,
  Spinner,
} from "reactstrap";

import axios from "../../../../utills/api";

import "./InvoiceModal.css";

import html2pdf from "html2pdf.js";

const InvoiceModal = ({
  isOpen,
  toggle,
  bookingId,
}) => {
  const [loading, setLoading] =
    useState(false);

  const [booking, setBooking] =
    useState(null);

  const [property, setProperty] =
    useState(null);

  const [foodOrders, setFoodOrders] =
    useState([]);

  const [kotOrders, setKotOrders] =
    useState([]);

  const [spaBookings, setSpaBookings] =
    useState([]);

  /* =========================================
     IMAGE BASE URL
  ========================================= */

  const IMAGE_BASE_URL =
    window.location.hostname ===
    "localhost"
      ? "http://localhost:5000"
      : "";

  /* =========================================
     LOAD INVOICE DATA
  ========================================= */

  useEffect(() => {
    if (!isOpen || !bookingId) {
      return;
    }

    fetchInvoiceData();
  }, [isOpen, bookingId]);

  const fetchInvoiceData =
    async () => {
      try {
        setLoading(true);

        /*
         * Clear old booking data immediately.
         */

        setBooking(null);
        setProperty(null);
        setFoodOrders([]);
        setKotOrders([]);
        setSpaBookings([]);

        const [
          checkoutRes,
          propertyRes,
          foodRes,
          kotRes,
          spaRes,
        ] = await Promise.all([
          axios.get(
            `/checkout/checkout/${bookingId}`
          ),

          axios.get(
            `/property-profile`
          ),

          axios.get(
            `/food/order`
          ),

          axios.get(
            `/pos/kot`
          ),

          axios.get(
            `/spa`
          ),
        ]);

        /* =========================================
           BOOKING
        ========================================= */

        const bookingData =
          checkoutRes?.data?.booking;

        if (!bookingData) {
          throw new Error(
            "Booking data not found"
          );
        }

        /* =========================================
           PROPERTY
        ========================================= */

        const propertyData =
          propertyRes?.data || {};

        /* =========================================
           ALL SERVICE DATA
        ========================================= */

        const allFoodOrders =
          Array.isArray(
            foodRes?.data
          )
            ? foodRes.data
            : [];

        const allKotOrders =
          Array.isArray(
            kotRes?.data
          )
            ? kotRes.data
            : [];

        const allSpaBookings =
          Array.isArray(
            spaRes?.data
          )
            ? spaRes.data
            : [];

        /* =========================================
           SET BOOKING + PROPERTY
        ========================================= */

        setBooking(
          bookingData
        );

        setProperty(
          propertyData
        );

        /* =========================================
           FOOD ORDERS
           
           ONLY bookingId
        ========================================= */

        const bookingFoodOrders =
          allFoodOrders.filter(
            (order) =>
              String(
                order?.bookingId ||
                  ""
              ) ===
              String(bookingId)
          );

        /* =========================================
           KOT ORDERS

           ONLY bookingId
        ========================================= */

        const bookingKotOrders =
          allKotOrders.filter(
            (kot) =>
              String(
                kot?.bookingId ||
                  ""
              ) ===
              String(bookingId)
          );

        /* =========================================
           SPA

           ONLY bookingId
        ========================================= */

        const bookingSpaBookings =
          allSpaBookings.filter(
            (spa) =>
              String(
                spa?.bookingId ||
                  ""
              ) ===
              String(bookingId)
          );

        console.log(
          "========== INVOICE DATA =========="
        );

        console.log(
          "Requested Booking:",
          bookingId
        );

        console.log(
          "Booking:",
          bookingData
        );

        console.log(
          "Property:",
          propertyData
        );

        console.log(
          "Hotel Logo:",
          propertyData?.profile
            ?.hotelLogo
        );

        console.log(
          "Authorized Signature:",
          propertyData?.profile
            ?.authorizedSignature
        );

        console.log(
          "Food Orders:",
          bookingFoodOrders
        );

        console.log(
          "KOT Orders:",
          bookingKotOrders
        );

        console.log(
          "SPA Bookings:",
          bookingSpaBookings
        );

        console.log(
          "=================================="
        );

        setFoodOrders(
          bookingFoodOrders
        );

        setKotOrders(
          bookingKotOrders
        );

        setSpaBookings(
          bookingSpaBookings
        );
      } catch (error) {
        console.error(
          "Invoice data error:",
          error
        );

        setBooking(null);
        setProperty(null);
        setFoodOrders([]);
        setKotOrders([]);
        setSpaBookings([]);
      } finally {
        setLoading(false);
      }
    };

  /* =========================================
     PROPERTY
  ========================================= */

  const propertyInfo =
    property?.client || {};

  const propertyProfile =
    property?.profile || {};

  /*
   * Property name
   */

  const propertyName =
    propertyInfo.propertyName ||
    propertyProfile.legalBusinessName ||
    "Property";

  /*
   * Property email
   */

  const propertyEmail =
    propertyInfo.adminEmail ||
    propertyInfo.email ||
    propertyProfile.adminEmail ||
    "";

  /*
   * Property mobile
   */

  const propertyMobile =
    propertyInfo.mobile ||
    propertyProfile.adminMobile ||
    "";

  /*
   * Property address
   */

  const propertyAddress = [
    propertyInfo.address,
    propertyInfo.city,
    propertyInfo.state,
    propertyInfo.country,
  ]
    .filter(Boolean)
    .join(", ");

  /*
   * GST
   */

  const gstNumber =
    propertyProfile?.gstNumber ||
    propertyInfo?.gstNumber ||
    property?.gstNumber ||
    "-";

  /*
   * PAN
   */

  const panNumber =
    propertyProfile?.panNumber ||
    propertyInfo?.panNumber ||
    property?.panNumber ||
    "-";

  /* =========================================
     HOTEL LOGO
  ========================================= */

  const hotelLogo =
    propertyProfile?.hotelLogo ||
    propertyInfo?.hotelLogo ||
    "";

  /*
   * Convert stored relative path:
   *
   * /uploads/profile/abc.jpg
   *
   * into:
   *
   * http://localhost:5000/uploads/profile/abc.jpg
   */

  const hotelLogoUrl =
    hotelLogo
      ? hotelLogo.startsWith(
          "http"
        )
        ? hotelLogo
        : `${IMAGE_BASE_URL}${hotelLogo}`
      : "";

  /* =========================================
     AUTHORIZED SIGNATURE
  ========================================= */

  const authorizedSignature =
    propertyProfile?.authorizedSignature ||
    propertyInfo?.authorizedSignature ||
    "";

  const authorizedSignatureUrl =
    authorizedSignature
      ? authorizedSignature.startsWith(
          "http"
        )
        ? authorizedSignature
        : `${IMAGE_BASE_URL}${authorizedSignature}`
      : "";

  /* =========================================
     CLIENT
  ========================================= */

  const clientName =
    booking?.name || "-";

  const clientEmail =
    booking?.email || "-";

  const clientMobile =
    booking?.mobile || "-";

  const clientAddress =
    booking?.address || "-";

  /* =========================================
     ROOM
  ========================================= */

  const roomNumber =
    booking?.assignedRoom
      ?.roomNumber ||
    booking?.assignedRoomNumber ||
    "-";

  const roomRate =
    Number(
      booking?.ratePerNight || 0
    );

  const nights =
    Number(
      booking?.chargeableNights ||
        booking?.stayedNights ||
        booking?.nights ||
        0
    );

  const roomSubtotal =
    Number(
      booking?.revisedRoomCharge ||
        booking?.roomCharge ||
        0
    );

  const roomTax =
    Number(
      booking?.revisedTaxAmount ||
        booking?.taxAmount ||
        0
    );

  const roomNet =
    roomSubtotal + roomTax;

  /* =========================================
     SERVICES
  ========================================= */

  const serviceRows = useMemo(() => {
    const rows = [];

    /*
     * FOOD ORDERS
     */

    foodOrders.forEach(
      (order) => {
        (
          order.items || []
        ).forEach((item) => {
          const quantity =
            Number(
              item.quantity || 0
            );

          const price =
            Number(
              item.price || 0
            );

          rows.push({
            service:
              "Food Order",

            item:
              item.name || "-",

            quantity,

            price,

            total:
              quantity * price,
          });
        });
      }
    );

    /*
     * KOT / RESTAURANT
     */

    kotOrders.forEach(
      (kot) => {
        (
          kot.items || []
        ).forEach((item) => {
          const quantity =
            Number(
              item.quantity || 0
            );

          const price =
            Number(
              item.price || 0
            );

          rows.push({
            service:
              "Restaurant",

            item:
              item?.name?.en ||
              item?.name ||
              "-",

            quantity,

            price,

            total:
              Number(
                item.total ||
                  quantity * price
              ),
          });
        });
      }
    );

    /*
     * SPA
     */

    spaBookings.forEach(
      (spa) => {
        const price =
          Number(
            spa.price || 0
          );

        rows.push({
          service: "Spa",

          item:
            spa.service ||
            "Spa Service",

          quantity: 1,

          price,

          total: price,
        });
      }
    );

    return rows;
  }, [
    foodOrders,
    kotOrders,
    spaBookings,
  ]);

  const serviceSubtotal =
    serviceRows.reduce(
      (sum, item) =>
        sum +
        Number(
          item.total || 0
        ),
      0
    );

  /*
   * Service tax = 5%
   */

  const serviceTax =
    serviceSubtotal * 0.05;

  const serviceNet =
    serviceSubtotal +
    serviceTax;

  /* =========================================
     PAYMENT
  ========================================= */

  const totalStayCharge =
    roomNet;

  const totalServiceCharge =
    serviceNet;

  const finalAmount =
    totalStayCharge +
    totalServiceCharge;

  const payments =
    booking?.payments || [];

  const finalPayment =
    [...payments]
      .reverse()
      .find(
        (payment) =>
          payment.paymentType ===
          "final"
      ) ||
    payments[
      payments.length - 1
    ];

  const finalPaidAmount =
    Number(
      finalPayment?.amount ||
        booking?.totalPaid ||
        0
    );

  const paymentMethod =
    finalPayment?.method ||
    "-";

  /* =========================================
     INVOICE DATE
  ========================================= */

  const invoiceDate =
    new Date().toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }
    );

  const formatDate = (
    date
  ) => {
    if (!date) return "-";

    return new Date(
      date
    ).toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  const money = (value) =>
    `₹${Number(
      value || 0
    ).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  /* =========================================
     PRINT / PDF
  ========================================= */

  const handlePrint =
    async () => {
      const element =
        document.getElementById(
          "invoice-print"
        );

      if (!element) return;

      /*
       * Make sure images are loaded
       * before html2pdf captures them.
       */

      const images =
        element.querySelectorAll(
          "img"
        );

      await Promise.all(
        Array.from(images).map(
          (img) => {
            if (
              img.complete
            ) {
              return Promise.resolve();
            }

            return new Promise(
              (resolve) => {
                img.onload =
                  resolve;

                img.onerror =
                  resolve;
              }
            );
          }
        )
      );

      const opt = {
        margin: [
          5,
          5,
          5,
          5,
        ],

        filename: `Invoice-${booking?.bookingId}.pdf`,

        image: {
          type: "jpeg",
          quality: 1,
        },

        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
        },

        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },

        pagebreak: {
          mode: [
            "avoid-all",
            "css",
            "legacy",
          ],
        },
      };

      await html2pdf()
        .set(opt)
        .from(element)
        .save();
    };

  /* =========================================
     MODAL
  ========================================= */

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      centered
      className="invoice-modal"
    >
      <ModalBody className="p-0">

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "500px",
            }}
          >
            <Spinner />
          </div>
        ) : !booking ? (
          <div className="text-center p-5">
            Unable to load invoice.
          </div>
        ) : (
          <>
            {/* =====================================
                ACTION BAR
            ====================================== */}

            <div className="invoice-actions d-flex justify-content-end gap-2 p-3">

              <Button
                color="secondary"
                outline
                onClick={toggle}
              >
                Close
              </Button>

              <Button
                color="primary"
                onClick={
                  handlePrint
                }
              >
                Print / Save PDF
              </Button>

            </div>

            {/* =====================================
                INVOICE
            ====================================== */}

            <div
              id="invoice-print"
              className="invoice-container"
            >

              {/* TOP BORDER */}

              <div className="invoice-top-border" />

              {/* =====================================
                  HEADER
              ====================================== */}

              <div className="invoice-header">

                <div>
                  <h1>
                    INVOICE
                  </h1>

                  <div className="invoice-number">
                    Invoice No:{" "}
                    {
                      booking.bookingId
                    }
                  </div>
                </div>

                {/* =================================
                    HOTEL LOGO + PROPERTY DETAILS
                ================================= */}

                <div className="invoice-property">

                  {hotelLogoUrl ? (
                    <img
                      src={
                        hotelLogoUrl
                      }
                      alt="Hotel Logo"
                      className="invoice-hotel-logo"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="invoice-hotel-logo-placeholder">
                      {propertyName
                        ?.charAt(
                          0
                        )
                        ?.toUpperCase()}
                    </div>
                  )}

                </div>

              </div>

              {/* =====================================
                  BILL TO / BILL FROM
              ====================================== */}

              <div className="invoice-two-column">

                <div>
                  <h4>
                    Bill To
                  </h4>

                  <strong>
                    Client Name:
                  </strong>{" "}
                  {
                    clientName
                  }
                  <br />

                  <strong>
                    Email:
                  </strong>{" "}
                  {
                    clientEmail
                  }
                  <br />

                  <strong>
                    Mobile:
                  </strong>{" "}
                  {
                    clientMobile
                  }
                  <br />

                  <strong>
                    Address:
                  </strong>{" "}
                  {
                    clientAddress
                  }
                </div>

                <div>
                  <h4>
                    Bill From
                  </h4>

                  <strong>
                    Property:
                  </strong>{" "}
                  {
                    propertyName
                  }
                  <br />

                  <strong>
                    Email:
                  </strong>{" "}
                  {
                    propertyEmail
                  }
                  <br />

                  <strong>
                    Address:
                  </strong>{" "}
                  {
                    propertyAddress
                  }
                  <br />

                  <strong>
                    GST No:
                  </strong>{" "}
                  {
                    gstNumber
                  }
                  <br />

                  <strong>
                    PAN No:
                  </strong>{" "}
                  {
                    panNumber
                  }
                </div>

              </div>

              {/* =====================================
                  STAY DETAILS
              ====================================== */}

              <section>

                <h2>
                  Stay Details
                </h2>

                <table className="invoice-table">

                  <thead>
                    <tr>

                      <th>
                        Date of Arrival
                      </th>

                      <th>
                        Date of Departure
                      </th>

                      <th>
                        Room
                      </th>

                      <th>
                        Nights
                      </th>

                      <th>
                        Room Rate / Night
                      </th>

                      <th>
                        Total
                      </th>

                    </tr>
                  </thead>

                  <tbody>

                    <tr>

                      <td>
                        {formatDate(
                          booking.checkIn
                        )}
                      </td>

                      <td>
                        {formatDate(
                          booking.checkOut
                        )}
                      </td>

                      <td>
                        {roomNumber}
                      </td>

                      <td>
                        {nights}
                      </td>

                      <td>
                        {money(
                          roomRate
                        )}
                      </td>

                      <td>
                        {money(
                          roomSubtotal
                        )}
                      </td>

                    </tr>

                  </tbody>

                </table>

                <div className="invoice-summary">

                  <div>
                    Subtotal

                    <span>
                      {money(
                        roomSubtotal
                      )}
                    </span>
                  </div>

                  <div>
                    Tax (
                    {
                      booking.gstRate ||
                      18
                    }
                    %)

                    <span>
                      {money(
                        roomTax
                      )}
                    </span>
                  </div>

                  <div className="invoice-total">

                    Net Stay Charge

                    <span>
                      {money(
                        totalStayCharge
                      )}
                    </span>

                  </div>

                </div>

              </section>

              {/* =====================================
                  SERVICE DETAILS
              ====================================== */}

              <section>

                <h2>
                  Service Details
                </h2>

                <table className="invoice-table">

                  <thead>

                    <tr>

                      <th>
                        No
                      </th>

                      <th>
                        Service
                      </th>

                      <th>
                        Item
                      </th>

                      <th>
                        Quantity
                      </th>

                      <th>
                        Price / Item
                      </th>

                      <th>
                        Total
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {serviceRows.length ===
                    0 ? (
                      <tr>

                        <td
                          colSpan="6"
                          className="text-center"
                        >
                          No services
                        </td>

                      </tr>
                    ) : (
                      serviceRows.map(
                        (
                          item,
                          index
                        ) => (
                          <tr
                            key={
                              index
                            }
                          >

                            <td>
                              {
                                index +
                                1
                              }
                            </td>

                            <td>
                              {
                                item.service
                              }
                            </td>

                            <td>
                              {
                                item.item
                              }
                            </td>

                            <td>
                              {
                                item.quantity
                              }
                            </td>

                            <td>
                              {money(
                                item.price
                              )}
                            </td>

                            <td>
                              {money(
                                item.total
                              )}
                            </td>

                          </tr>
                        )
                      )
                    )}

                  </tbody>

                </table>

                <div className="invoice-summary">

                  <div>

                    Subtotal

                    <span>
                      {money(
                        serviceSubtotal
                      )}
                    </span>

                  </div>

                  <div>

                    Tax (5%)

                    <span>
                      {money(
                        serviceTax
                      )}
                    </span>

                  </div>

                  <div className="invoice-total">

                    Net Service Charge

                    <span>
                      {money(
                        totalServiceCharge
                      )}
                    </span>

                  </div>

                </div>

              </section>

              {/* =====================================
                  PAYMENT DETAILS
              ====================================== */}

              <section>

                <h2>
                  Payment Details
                </h2>

                <div className="payment-box">

                  <div>

                    <span>
                      Total Stay Charge
                    </span>

                    <strong>
                      {money(
                        totalStayCharge
                      )}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Total Service Charge
                    </span>

                    <strong>
                      {money(
                        totalServiceCharge
                      )}
                    </strong>

                  </div>

                  <div className="final-payment">

                    <span>
                      Final Paid Amount
                    </span>

                    <strong>
                      {money(
                        finalPaidAmount
                      )}
                    </strong>

                  </div>

                </div>

                <div className="payment-information">

                  <div>

                    <strong>
                      Payment Type:
                    </strong>{" "}

                    {
                      paymentMethod
                    }

                  </div>

                  <div className="invoice-generated">

                    <strong>
                      Invoice Date:
                    </strong>{" "}

                    {
                      invoiceDate
                    }

                  </div>

                </div>

                {/* =================================
                    FOOTER + SIGNATURE
                ================================== */}

                <div className="invoice-footer">

                  {/* LEFT */}

                  <div>

                    <h4>
                      Questions?
                    </h4>

                    <div>
                      Email us:{" "}
                      {
                        propertyEmail
                      }
                    </div>

                    {propertyMobile && (
                      <div>
                        Call us:{" "}
                        {
                          propertyMobile
                        }
                      </div>
                    )}

                  </div>

                  {/* RIGHT */}

                  <div className="client-signature">

                    {authorizedSignatureUrl ? (
                      <img
                        src={
                          authorizedSignatureUrl
                        }
                        alt="Authorized Signature"
                        className="invoice-signature-image"
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div className="signature-line" />
                    )}

                    <strong>
                      {
                        propertyName
                      }
                    </strong>

                  </div>

                </div>

              </section>

              {/* =====================================
                  BOTTOM BORDER
              ====================================== */}

              <div className="invoice-bottom-border" />

            </div>
          </>
        )}

      </ModalBody>
    </Modal>
  );
};

export default InvoiceModal;